import { ArrowUp } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { TestCall } from "@/components/landing/discovery/test-call"
import { type Lookup } from "@/components/landing/discovery/use-assessment-lookup"
import { type PartnerInfo } from "@/components/landing/hub/types"

/**
 * IMP-531: the interim train-and-test-call conversation — the discovery
 * interview's stand-in while discovery gets nailed down. No questions are
 * asked anywhere: Iza introduces herself, reads the site behind one
 * shimmering status line, announces what she learned (naming every
 * degradation out loud — never a confident wrong answer), hands the visitor
 * a test call, then captures email as delivery of a link to call her again.
 * The mock on partner-landing-page design/test-agent is the spec, including
 * all conversation copy.
 */

interface ChatMessage {
  from: "assistant" | "user"
  text: string
  kind?: "call"
}

/** Typing beat before every assistant message (mock: 950ms). */
const TYPING_MS = 950
/** The shimmer reads for at least this long, even when the lookup is
 *  already settled — a read that flashes by reads as fake. */
const MIN_READ_MS = 2500

export function TestAgentConversation({
  landingUid,
  site,
  lookup,
  partner,
  purchasable,
  onEmail,
  onAccountLanding,
  onPurchaseOpen,
}: {
  landingUid: string
  /** The visitor's site, scheme/www stripped — echoed in the copy. */
  site: string
  lookup: Lookup
  partner: PartnerInfo
  /** Whether the pinned purchase bar can appear — a partner with nothing
   *  purchasable keeps the flow, and the closing line drops "get started
   *  below" (an ask the page can't honour). */
  purchasable: boolean
  onEmail: (email: string) => void
  onAccountLanding: (accountLandingUid: string) => void
  onPurchaseOpen: () => void
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [typing, setTyping] = useState(false)
  const [reading, setReading] = useState(false)
  const [callOffer, setCallOffer] = useState(false)
  const [emailMode, setEmailMode] = useState(false)
  const [emailText, setEmailText] = useState("")
  // "full" | "partial" | "failed" — remembered for the email confirmation.
  const pathRef = useRef<"full" | "partial" | "failed" | null>(null)
  const openedRef = useRef(false)
  const readStartedAt = useRef(0)
  const branchedRef = useRef(false)
  const closeStarted = useRef(false)
  const timers = useRef<number[]>([])
  const endRef = useRef<HTMLDivElement>(null)

  function later(ms: number, fn: () => void) {
    timers.current.push(window.setTimeout(fn, ms))
  }

  function say(text: string, after?: () => void) {
    setTyping(true)
    later(TYPING_MS, () => {
      setTyping(false)
      setMessages((prev) => [...prev, { from: "assistant", text }])
      after?.()
    })
  }

  useEffect(() => {
    const tracked = timers.current
    return () => {
      // StrictMode-safe: the simulated unmount clears pending timers, so the
      // once-guards must re-arm or the remounted effect would never re-run
      // and the intro's typing dots would hang forever.
      tracked.forEach(clearTimeout)
      tracked.length = 0
      openedRef.current = false
      branchedRef.current = false
    }
  }, [])

  // Intro, then the shimmer while the real site lookup runs.
  useEffect(() => {
    if (openedRef.current) return
    openedRef.current = true
    const name = partner.assistant_name || "your assistant"
    say(
      `Hi, I'm ${name}, an AI assistant from ${partner.name}. I'm reading your website right now to learn your business.`,
      () => {
        readStartedAt.current = Date.now()
        setReading(true)
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Branch when the lookup settles (holding the shimmer for the minimum
  // beat). The three paths are the entire degradation story.
  useEffect(() => {
    if (branchedRef.current || !reading) return
    if (lookup.status === "pending") return
    branchedRef.current = true

    const elapsed = Date.now() - readStartedAt.current
    later(Math.max(0, MIN_READ_MS - elapsed), () => {
      setReading(false)
      const profile = lookup.profile
      if (lookup.status === "succeeded" && profile) {
        // Only a sparse services list demotes the read: most sites don't
        // publish hours, so missing hours alone would send nearly everyone
        // down the thin path and undercut a perfectly good read.
        const thin = profile.services.length < 3
        if (!thin) {
          pathRef.current = "full"
          say(
            `I read through ${site} and learned what I could about ${profile.business_name}. I'm ready to answer your phone. Want to hear how I'd do it?`,
            () => setCallOffer(true),
          )
        } else {
          pathRef.current = "partial"
          say(
            `Your site and public listings didn't give me much to work with. I found your name and what you do, ${thinGaps(profile.hours)}`,
            () =>
              say(
                "I learned what I could from what's there. Anything I don't know, I take a message instead of guessing. Want to hear how I'd answer your phone?",
                () => setCallOffer(true),
              ),
          )
        }
      } else {
        // A failed read closes the demo path: no call offer, no purchase
        // bar — email becomes the follow-up channel.
        pathRef.current = "failed"
        say(
          "Hmm, I wasn't able to read your site. Some websites block automated visitors, and some just don't say much.",
          () =>
            say(
              "Leave your email and we'll take a look ourselves. You'll get a link to try me out shortly.",
              () => setEmailMode(true),
            ),
        )
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookup.status, reading])

  // Honest thin-read clause: the thin path always means a sparse services
  // list, so the clause names that, plus hours when those are missing too.
  function thinGaps(hours: string | null): string {
    if (!hours)
      return "but I couldn't find your hours, and only a couple of services."
    return "but only found a couple of services."
  }

  function startCall() {
    setCallOffer(false)
    setMessages((prev) => [
      ...prev,
      { from: "user", text: "Start test call" },
      { from: "assistant", text: "", kind: "call" },
    ])
  }

  function endCall() {
    if (closeStarted.current) return
    closeStarted.current = true
    const business = lookup.profile?.business_name ?? "your business"
    say(
      `That's how I'd answer for ${business}. I can qualify leads, book appointments, transfer calls, and take messages. Anything I didn't know yet, you can teach me when you get started. It only takes a few minutes.`,
      () =>
        say(
          "I'll send you a link so you can call me again or share me with your team. Where should I send it?",
          () => {
            setEmailMode(true)
            // The ask opens at the value peak — with the email ask, not
            // after it. Never on the failed path.
            if (purchasable) onPurchaseOpen()
          },
        ),
    )
  }

  function submitEmail() {
    const text = emailText.trim()
    if (!text.includes("@")) return
    setEmailMode(false)
    setEmailText("")
    setMessages((prev) => [...prev, { from: "user", text }])
    onEmail(text)
    if (pathRef.current === "failed") {
      say("Got it. We'll take it from here, and you'll hear from us shortly.")
    } else if (purchasable) {
      say(
        "Got it, you should receive an email shortly. When you're ready, get started below.",
      )
    } else {
      say("Got it, you should receive an email shortly.")
    }
  }

  // Buttons, the shimmer, and the call panel arrive without a message
  // change, hence the extra deps.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
  }, [messages, typing, reading, callOffer, emailMode])

  return (
    <div className="flex flex-col gap-2.5">
      {messages.map((message, index) =>
        message.kind === "call" ? (
          <TestCall
            key={index}
            landingUid={landingUid}
            assessmentUid={lookup.assessmentUid}
            business={lookup.profile?.business_name ?? site}
            onAccountLanding={onAccountLanding}
            onEnd={endCall}
          />
        ) : (
          <p
            key={index}
            className={
              message.from === "assistant"
                ? "lp-msg-in text-lp-fg max-w-[85%] self-start py-1 text-[15px] leading-[1.6]"
                : "lp-msg-in text-lp-fg bg-lp-surface max-w-[85%] self-end rounded-2xl rounded-br-md px-4 py-2.5 text-[15px] leading-[1.6]"
            }
          >
            {message.text}
          </p>
        ),
      )}

      {reading && (
        <p className="lp-shimmer self-start text-[13.5px]">Reading {site}…</p>
      )}

      {typing && (
        <div className="lp-msg-in flex gap-1 self-start px-0.5 py-2.5">
          {[0, 0.15, 0.3].map((delay) => (
            <span
              key={delay}
              className="lp-dot bg-lp-muted/60 size-1.5 rounded-full"
              style={{ animationDelay: `${delay}s` }}
            />
          ))}
        </div>
      )}

      {callOffer && (
        <button
          type="button"
          onClick={startCall}
          className="lp-msg-in border-lp-border text-lp-fg hover:border-lp-border-strong mt-1 h-11 self-start rounded-full border bg-white px-6 text-[14px] font-medium transition-colors"
        >
          Start a test call
        </button>
      )}

      {emailMode && (
        <form
          className="lp-msg-in border-lp-border focus-within:border-lp-border-strong mt-1 flex w-full max-w-sm items-center gap-2 self-start rounded-full border bg-white py-1.5 pr-1.5 pl-4 transition-colors"
          onSubmit={(event) => {
            event.preventDefault()
            submitEmail()
          }}
        >
          <input
            type="email"
            value={emailText}
            onChange={(event) => setEmailText(event.target.value)}
            aria-label="Your email"
            placeholder="you@yourbusiness.com"
            // border-0/p-0/focus:ring-0 undo the @tailwindcss/forms base
            // chrome (gray border, padding, blue focus ring) — the pill is
            // the visible field; the input inside it must stay bare.
            className="text-lp-fg placeholder:text-lp-muted/60 h-9 min-w-0 flex-1 border-0 bg-transparent p-0 text-[14px] outline-none focus:ring-0"
          />
          <button
            type="submit"
            aria-label="Send"
            className="bg-lp-fg flex size-8 shrink-0 items-center justify-center rounded-full text-white transition-colors hover:bg-black"
          >
            <ArrowUp className="size-4" />
          </button>
        </form>
      )}

      <div ref={endRef} className="scroll-mb-44" />
    </div>
  )
}
